import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ClientView from './ClientView';
import { createMaythaiDemoRuntime, MAYTHAI_DEMO_UUID, MaythaiDemoIdentity } from './maythaiDemoRuntime';
import './MaythaiDemo.css';

export default function MaythaiDemo() {
  const [catalogue, setCatalogue] = useState(null);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [stage, setStage] = useState('attract');
  const [generation, setGeneration] = useState(0);
  const [mediaAllowed, setMediaAllowed] = useState(() => !document.hidden && !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
  const backgroundVideo = useRef(null);
  const session = useRef(null);
  if (!session.current) {
    const candidate = new URLSearchParams(window.location.search).get('demoSession');
    session.current = MAYTHAI_DEMO_UUID.test(candidate || '') ? candidate : window.crypto.randomUUID();
  }
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    setError('');
    // The sole API call for this experience. Never attach operator cookies,
    // bearer tokens, device credentials or a visitor's phone number.
    fetch('/api/maythai-demo/catalogue', { method: 'GET', credentials: 'omit', cache: 'no-store', signal: controller.signal })
      .then(async response => {
        const payload = await response.json();
        if (!response.ok || !Array.isArray(payload?.sections) || !Array.isArray(payload?.products) || !Array.isArray(payload?.menus) || !payload?.settings) throw new Error('La carte de démonstration est momentanément indisponible.');
        if (active) setCatalogue(payload);
      }).catch(() => { if (active) setError('La carte de démonstration est momentanément indisponible. Réessaie dans un instant.'); })
      .finally(() => clearTimeout(timeout));
    return () => { active = false; controller.abort(); clearTimeout(timeout); };
  }, [attempt]);
  useEffect(() => {
    const motion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const refresh = () => setMediaAllowed(!document.hidden && !motion?.matches);
    motion?.addEventListener('change', refresh);
    document.addEventListener('visibilitychange', refresh);
    refresh();
    return () => { motion?.removeEventListener('change', refresh); document.removeEventListener('visibilitychange', refresh); };
  }, []);
  const onNavigate = useCallback(path => {
    if (path === '/veille') { setGeneration(value => value + 1); setStage('attract'); }
    else if (path === '/welcome') { setGeneration(value => value + 1); setStage('welcome'); }
  }, []);
  const runtime = useMemo(() => catalogue ? createMaythaiDemoRuntime({ catalogue, sessionId: session.current, onNavigate,
    onConfirm: order => {
      if (window.parent !== window) window.parent.postMessage({ type: 'maythai:order-confirmed', version: 1, sessionId: session.current, order }, window.location.origin);
    },
  }) : null, [catalogue, onNavigate]);
  const settings = catalogue?.settings || {};
  const theme = {
    '--kiosk-primary': settings.uiPrimaryColor || '#08383a', '--kiosk-accent': settings.uiAccentColor || '#ccb08c',
    '--kiosk-ink': settings.uiTextColor || '#2b180f', '--kiosk-bg': settings.uiBackgroundColor || '#f7f0e4',
    '--kiosk-surface': settings.uiSurfaceColor || 'rgba(255, 250, 243, 0.96)', '--kiosk-muted': settings.uiMutedColor || '#7d6757',
  };
  const logo = settings.displayLogoMedia;
  const useAttractBackground = stage === 'attract' || settings.clientBackgroundEnabled === false;
  const background = useAttractBackground ? settings.attractBackgroundMedia : (settings.clientBackgroundMedia || settings.attractBackgroundMedia);
  const backgroundType = useAttractBackground ? settings.attractBackgroundType : (settings.clientBackgroundType || settings.attractBackgroundType);
  useEffect(() => {
    const video = backgroundVideo.current;
    if (!video) return undefined;
    if (mediaAllowed) Promise.resolve(video.play()).catch(() => {});
    else video.pause();
    return () => video.pause();
  }, [mediaAllowed, background, backgroundType, stage]);
  if (!catalogue) return <main className="maythai-demo-loading"><h1>Teste l’interface MAYTHAI</h1><p role={error ? 'alert' : 'status'}>{error || 'Préparation de ta borne…'}</p>{error && <button type="button" className="kiosk-cta" onClick={() => setAttempt(value => value + 1)}>Réessayer</button>}</main>;
  return <div className="maythai-demo" style={theme}>
    {background && <div className="maythai-demo-background" aria-hidden="true">{backgroundType === 'video'
      ? <video ref={backgroundVideo} src={background} autoPlay={mediaAllowed} muted loop playsInline preload={mediaAllowed ? 'metadata' : 'none'} /> : <img src={background} alt="" />}</div>}
    {stage === 'attract' && <button type="button" className="attract-screen maythai-demo-attract" onClick={() => setStage('welcome')}>
      <div className="attract-brand-logo">{logo ? <img src={logo} alt="MAYTHAI" /> : <strong>MAYTHAI</strong>}</div>
      <div className="attract-message" style={{ color: settings.attractTextColor || '#fff8ef', fontSize: `${settings.attractFontSize || 72}px` }}><span>{settings.attractMessage || 'TOUCHE POUR COMMANDER'}</span></div>
      <span className="maythai-demo-note">Démonstration interactive</span>
    </button>}
    {stage === 'welcome' && <section className="loyalty-entry-page"><div className="loyalty-entry-card">
      {logo && <img className="maythai-demo-logo" src={logo} alt="MAYTHAI" />}<span className="kiosk-eyebrow">Bienvenue chez MAYTHAI</span><h1>Mon compte</h1>
      <MaythaiDemoIdentity key={generation} onContinue={() => setStage('catalogue')} />
    </div></section>}
    {stage === 'catalogue' && <ClientView key={generation} demoRuntime={runtime} />}
  </div>;
}
